<script setup>
import { ref, onMounted } from 'vue';
import { useAgentsStore } from '@/stores/agents';
import { storeToRefs } from 'pinia';

const params = ref({
    term: '',
    page: 1,
    per_page: 10,
    sort_by: null,
});

const agentsStore = useAgentsStore();
const { allAgents } = storeToRefs(agentsStore);
const fetchAgents = () => {
    agentsStore.searchAgents({
        ...params.value,
    });
};

const clearSearch = () => {
    params.value.term = '';
    fetchAgents();
};

const tableUpdate = ({ page, sortBy, itemsPerPage, sortDesc }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage, sort_desc: sortDesc });
    fetchAgents();
};

const accessAgent = (agent) => {
    agentsStore.impersonate(agent.hash).then(() => {
        window.location.href = '/offers';
    });
};

onMounted(() => {
    fetchAgents();
});

</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('agent.agent_management') }}</template>

    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="fetchAgents"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />
        <StyledDropdown
            v-model="params.status"
            clearable
            :placeholder="$t('label.is_active')"
            :options="[
                { label: $t('label.yes'), value: 'true' },
                { label: $t('label.no'), value: 'false' }
            ]"
            name="status"
            @input="fetchAgents"
            @clear="fetchAgents" />    
    </div>

    <DataTable
        v-if="allAgents"
        :data="allAgents"
        :headers="[
            { title: $t('label.name'), key: 'full_name', value: item => `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim(), sortable: false },
            { title: $t('label.email'), key: 'email', value: item => item.email, sortable: false },
            { title: $t('label.phone_number') || 'Phone Number', key: 'phone_number', value: item => item.phone_number || '', sortable: false },
            { title: $t('label.company') || 'Company', key: 'company_name', value: item => item.company_name || item.company || '', sortable: false },
            { title: $t('label.last_login') || 'Last Login', key: 'last_login_log', value: item => (item.last_login_log && item.last_login_log.created_at) ? new Date(item.last_login_log.created_at).toLocaleString() : '', sortable: false },
            { title: $t('label.subscription') || 'Subscription', key: 'subscription', value: item => {
                if (Array.isArray(item.subscriptions) && item.subscriptions.length) {
                    const names = item.subscriptions.map(s => {
                        return s.plan?.name || s.name || null;
                    }).filter(Boolean);
                    return names.length ? names.join(', ') : $t('label.no_subscription_yet');
                }
                return $t('label.no_subscription_yet');
            }, sortable: false },
            { title: $t('label.status'), key: 'status', value: item => item.is_active ? $t('label.active') : $t('label.inactive'), sortable: false },
        ]"
        :actions="[
            { title: ($t('agent.access_agent') || 'Access Agent'), icon: 'user-shield', action: (item) => accessAgent(item) },
        ]"
        @table-update="tableUpdate" />
</MainContentLayout>
</template>
